import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router-dom";
import { z } from "zod";
import {
    useChangeRoleMutation,
    useFindOneQuery,
    useUpdateMutation,
} from "../../../../features/usersApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks";
import { Layout } from "../../../ui/layout";
import Avatar from "../../../ui/molecules/avatar";
import Button from "../../../ui/molecules/button";
import Input from "../../../ui/molecules/input";
import { SelectCustom } from "../../../ui/molecules/select";
import { Spinner } from "../../../ui/molecules/spinner";
import Toggle from "../../../ui/molecules/toggle";
import FileUploader from "../../../ui/organisms/fileUploader";
import { MenuItems } from "../../../ui/organisms/navbar/dto";
import { CustomerAccessEnum, UserRole, UserRoleMap } from "../dto";
import { SelectUser } from "../select/userSelect";
import { roleUpdateSchema, rolesMap, updateUserSchema } from "./validation";

type UpdateUserFormValues = z.infer<typeof updateUserSchema>;
type RoleUpdateFormValues = z.infer<typeof roleUpdateSchema>;

export function UserEdit() {
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();
    const { data: userResponse, isLoading: loadingUser } = useFindOneQuery(id!);
    const userData = userResponse?.user;
    const existingAvatar = userResponse?.avatar;
    const [updateUser] = useUpdateMutation();
    const [changeRole] = useChangeRoleMutation();
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string>("");

    const form = useForm<UpdateUserFormValues>({
        resolver: zodResolver(updateUserSchema),
        defaultValues: {
            name: "",
            surname: "",
            phone: "",
            companyVat: "",
            companyName: "",
            companyWebsite: "",
            companyRevenue: undefined,
            isAutonomous: false,
            collaborators: [],
            customerId: "",
            companyAtecoCode: "",
            companySector: "",
            customerAccess: null,
        },
    });

    const roleForm = useForm<RoleUpdateFormValues>({
        resolver: zodResolver(roleUpdateSchema),
        defaultValues: {
            role: UserRole.customer,
            teamLeaderId: undefined,
        },
    });

    const role = roleForm.watch("role");
    const { watch } = form;

    const toValue = (v: any) => {
        if (v == null) return undefined;
        if (Array.isArray(v)) return v.map((it) => (it?.value ?? it));
        return v.value ?? v;
    };

    useEffect(() => {
        if (userData) {
            const formValues: Partial<UpdateUserFormValues> = {
                name: userData.name,
                surname: userData.surname,
                phone: userData.phone,
                companyVat: userData.companyVat,
                companyName: userData.companyName,
                companyWebsite: userData.companyWebsite,
                companyRevenue: userData.companyRevenue,
                isAutonomous: userData.isAutonomous,
                collaborators: userData.collaborators || [],
                customerId: userData.customerId || "",
                companyAtecoCode: userData.companyAtecoCode,
                companySector: userData.companySector,
                customerAccess: userData.customerAccess,
            };
            form.reset(formValues);

            const roleValues: Partial<RoleUpdateFormValues> = {
                role: userData.role,
                teamLeaderId: (userData as any).teamLeaderId || undefined,
            };
            roleForm.reset(roleValues);

            if (existingAvatar) {
                setPreview(existingAvatar);
            } else {
                setPreview("");
            }
        }
    }, [userData, existingAvatar, form, roleForm]);

    useEffect(() => {
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    }, [file]);

    // const hasUserChanges = useMemo(() => {
    //     if (!userData) return false;
    //     const current = form.getValues();

    //     const fields = ["name", "surname", "phone", "companyVat", "companyName", "companyWebsite", "isAutonomous", "companyRevenue", "collaborators", "customerId", "companyAtecoCode", "companySector", "customerAccess"];
    //     const fieldsChanged = fields.some(
    //         (f) =>
    //             current[f as keyof UpdateUserFormValues] !==
    //             (userData as any)[f]
    //     );

    //     return fieldsChanged || !!file;
    // }, [form.watch(), file, userData]);

    const hasRoleChange = useMemo(() => {
        const currentRole = roleForm.getValues("role");
        const currentTeamLeaderId = roleForm.getValues("teamLeaderId");
        const originalTeamLeaderId = (userData as any)?.teamLeaderId;
        return (
            currentRole !== userData?.role ||
            currentTeamLeaderId !== originalTeamLeaderId
        );
    }, [roleForm.watch("role"), roleForm.watch("teamLeaderId"), userData]);

    const atecos = useAppSelector((s) => s.atecos.findAllAtecosResponse ?? []);

    // const isDisabled =
    //     (!hasUserChanges && !hasRoleChange) ||
    //     updateRes.isLoading ||
    //     changeRoleRes.isLoading;

    const handleSubmit = async () => {
        const formData = form.getValues();
        const roleData = roleForm.getValues();

        const updateData = {
            name: formData.name,
            surname: formData.surname,
            phone: formData.phone,
            companyVat: formData.companyVat,
            companyName: formData.companyName,
            companyWebsite: formData.companyWebsite,
            companyRevenue: formData.companyRevenue,
            isAutonomous: formData.isAutonomous,
            collaborators: formData.collaborators || [],
            customerId: formData.customerId || "",
            companyAtecoCode: formData.companyAtecoCode,
            companySector: formData.companySector,
            customerAccess: formData.customerAccess,
        };

        // if (hasUserChanges) {
        try {
            const form_data = new FormData();
            if (file) form_data.append("file", file);
            form_data.append("user", JSON.stringify(updateData));

            await updateUser({
                id: userData!.id,
                data: form_data,
            }).unwrap();

            dispatch(addBannerWithTimeout({
                message: "Dati utente aggiornati con successo",
                variant: "success"
            }));
        } catch (error) {
            dispatch(addBannerWithTimeout({
                message: "Errore nell'aggiornamento dei dati utente",
                variant: "error"
            }));
        }

        if (hasRoleChange) {
            try {
                await changeRole({
                    id: userData!.id,
                    role: roleData.role,
                    teamLeaderId: roleData.teamLeaderId,
                }).unwrap();

                dispatch(addBannerWithTimeout({
                    message: "Ruolo utente cambiato con successo",
                    variant: "success"
                }));
            } catch (error) {
                const errors = error as { data: { errors: { message: string }[] }, status: number }
                let message = "Errore nel cambio ruolo utente"
                if (errors && errors.data && errors.data.errors && errors.data.errors.length > 0) {
                    if (errors.data.errors.map(e => e.message).includes("team leader id is null")) {
                        message = "È necessario selezionare un team leader valido"
                    }
                }
                dispatch(addBannerWithTimeout({
                    message,
                    variant: "error"
                }));
            }
        }
    };

    if (loadingUser) {
        return <div className="flex h-screen w-full justify-center items-center"><Spinner /></div>
    }

    const companyAtecoCode = watch("companyAtecoCode");
    const companySector = companyAtecoCode
        ? atecos.find((a: any) => a.atecoCode === companyAtecoCode)?.sector
        : undefined;

    return (
        <Layout
            key={"edit-user"}
            menuItem={MenuItems.USERS}
            breadcrumbItems={["Utenti", "Modifica utente"]}
            headerBackPath={"/users"}
            headerLabel={
                userData?.name + " " + userData?.surname || "Modifica utente"
            }
            headerChildren={
                <div className="flex items-center h-[100%] w-[100%] justify-end">
                    <Button
                        size={"sm"}
                        iconPosition={"off"}
                        label="Aggiorna"
                        variant={"solid"}
                        color={"blue"}
                        onClick={form.handleSubmit(handleSubmit)}
                    // disabled={isDisabled}
                    />
                </div>
            }
        >
            <div className={"gap-[24px] max-h-full flex flex-col w-[100%] h-full p-[16px]"}>
                <div className="shadow-sm p-[24px] h-full max-h-full overflow-auto gap-[20px] flex flex-col bg-white rounded-[12px]">
                    {/* Ruolo */}
                    <div className="flex gap-[24px] w-[100%] items-start">
                        <div className="w-[240px] text-label-md font-medium text-neutral-600">Ruolo</div>
                        <div className="flex gap-[16px] w-[100%]">
                            <Controller
                                name="role"
                                control={roleForm.control}
                                render={({ field }) => (
                                    <SelectCustom
                                        defaultValue={field.value}
                                        onChange={(v) => field.onChange(toValue(v))}
                                        error={!!roleForm.formState.errors.role}
                                        errorLabel={String(roleForm.formState.errors.role?.message ?? "")}
                                        placeholder="Seleziona un ruolo"
                                        options={
                                            rolesMap
                                                .get(userData?.role!)
                                                ?.map((_role) => ({
                                                    label: UserRoleMap.get(_role)!,
                                                    value: _role,
                                                }))!
                                        }
                                    />
                                )}
                            />
                            {role === UserRole.commercial && (
                                <div className="min-w-[150px] flex">
                                    <Controller
                                        name="isAutonomous"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Toggle
                                                checked={field.value || false}
                                                onChange={field.onChange}
                                                placeholder="È autonomo"
                                            />
                                        )}
                                    />
                                </div>
                            )}
                            {role === UserRole.customer && (
                                <>
                                    <div className="min-w-[280px] flex">
                                        <Controller
                                            name="customerAccess"
                                            control={form.control}
                                            render={({ field }) => (
                                                <Toggle
                                                    placeholder="Può accedere alle liste target"
                                                    checked={field.value !== CustomerAccessEnum.NONE}
                                                    onChange={(e) => field.onChange(e ? CustomerAccessEnum.PARTIAL : CustomerAccessEnum.NONE)}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="min-w-[320px] flex">
                                        <Controller
                                            name="customerAccess"
                                            control={form.control}
                                            render={({ field }) => (
                                                <Toggle
                                                    placeholder="Ha accesso completo ai contatti"
                                                    disabled={field.value === CustomerAccessEnum.NONE}
                                                    checked={field.value === CustomerAccessEnum.FULL}
                                                    onChange={(e) => field.onChange(e ? CustomerAccessEnum.FULL : CustomerAccessEnum.PARTIAL)}
                                                />
                                            )}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {
                        userData?.role === UserRole.team_leader &&
                        role !== UserRole.team_leader && (
                            <div className="flex gap-[24px] w-[100%] items-start">
                                <div className="w-[240px] text-label-md font-medium text-neutral-600">
                                    Team Leader
                                </div>
                                <div className="flex gap-[16px] w-[100%]">
                                    <Controller
                                        name="teamLeaderId"
                                        control={roleForm.control}
                                        render={({ field }) => (
                                            <SelectUser
                                                isMulti={false}
                                                id="select-team-leader"
                                                role={UserRole.team_leader}
                                                value={field.value || null}
                                                onChange={(v) => field.onChange(v || null)}
                                                placeholder="Seleziona il team leader che prenderà in carico i progetti"
                                                filterUsers={users => users.filter(user => user.id !== userData?.id)}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                    <div className="flex gap-[24px] w-[100%] items-start">
                        <div className="w-[240px] text-text-md font-semibold text-neutral-600">Email</div>
                        <div className="flex gap-[16px] w-[100%]">
                            <Input
                                disabled
                                value={userData?.email || ""}
                            />
                        </div>
                    </div>
                    <div className="flex gap-[24px] w-[100%] items-start">
                        <div className="w-[240px] text-text-md font-semibold text-neutral-600">Nome</div>
                        <div className="flex gap-[16px] w-[100%]">
                            <Controller
                                name="name"
                                control={form.control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        value={field.value || ""}
                                        placeholder="Nome"
                                        supportingText={form.formState.errors.name?.message as string | undefined}
                                        error={!!form.formState.errors.name}
                                    />
                                )}
                            />
                            <Controller
                                name="surname"
                                control={form.control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        value={field.value || ""}
                                        placeholder="Cognome"
                                        supportingText={form.formState.errors.surname?.message as string | undefined}
                                        error={!!form.formState.errors.surname}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    {/* Telefono */}
                    <div className="flex gap-[24px] w-[100%] items-start">
                        <div className="w-[240px] text-text-md font-semibold text-neutral-600">Smartphone</div>
                        <div className="flex gap-[16px] w-[100%]">
                            <Controller
                                name="phone"
                                control={form.control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        value={field.value || ""}
                                        placeholder="Smartphone"
                                        supportingText={form.formState.errors.phone?.message as string | undefined}
                                        error={!!form.formState.errors.phone}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    {role === UserRole.customer && (
                        <>
                            {/* Collaboratori */}
                            <div className="flex gap-[24px] w-[100%] items-start">
                                <div className="w-[240px] text-label-md font-medium text-neutral-600">Collaboratori</div>
                                <div className="flex gap-[16px] w-[100%]">
                                    <Controller
                                        name="collaborators"
                                        control={form.control}
                                        render={({ field }) => (
                                            <SelectUser
                                                isMulti={true}
                                                id="select-collaborators"
                                                role={UserRole.collaborator}
                                                value={field.value || undefined}
                                                onChange={(v) => field.onChange(v || [])}
                                                placeholder="Seleziona collaboratori"
                                                isClearable
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Company name */}
                            <div className="flex gap-[24px] w-[100%] items-start">
                                <div className="w-[240px] text-label-md font-medium text-neutral-600">Nome azienda <span className="text-red-600"> *</span></div>
                                <div className="flex gap-[16px] w-[100%]">
                                    <Controller
                                        name="companyName"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Input {...field} value={field.value || ""} placeholder="Nome azienda" supportingText={form.formState.errors.companyName?.message as string | undefined} error={!!form.formState.errors.companyName} />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* VAT */}
                            <div className="flex gap-[24px] w-[100%] items-start">
                                <div className="w-[240px] text-label-md font-medium text-neutral-600">P. IVA azienda</div>
                                <div className="flex gap-[16px] w-[100%]">
                                    <Controller
                                        name="companyVat"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Input {...field} value={field.value || ""} placeholder="P. IVA" supportingText={form.formState.errors.companyVat?.message as string | undefined} error={!!form.formState.errors.companyVat} />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* ATECO */}
                            <div className="flex gap-[24px] w-[100%] items-start">
                                <div className="w-[240px] text-label-md font-medium text-neutral-600">Codice ATECO</div>
                                <div className="flex gap-[16px] w-[100%]">
                                    <Controller
                                        name="companyAtecoCode"
                                        control={form.control}
                                        render={({ field }) => {
                                            return (
                                                <SelectCustom
                                                    placeholder="Cerca per ATECO"
                                                    isClearable
                                                    defaultValue={field.value ?? undefined}
                                                    onChange={(v) => field.onChange(toValue(v))}
                                                    options={atecos.map((ateco: any) => ({ value: ateco.atecoCode, label: `${ateco.atecoCode} - ${ateco.atecoDescription}` }))}
                                                />
                                            );
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Settore */}
                            <div className="flex gap-[24px] w-[100%] items-start">
                                <div className="w-[240px] text-label-md font-medium text-neutral-600">Settore</div>
                                <div className="flex gap-[16px] w-[100%]">
                                    <Input value={companySector} placeholder="Settore" disabled supportingText={form.formState.errors.companySector?.message as string | undefined} error={!!form.formState.errors.companySector} />
                                </div>
                            </div>

                            {/* Revenue numeric */}
                            <div className="flex gap-[24px] w-[100%] items-start">
                                <div className="w-[240px] text-label-md font-medium text-neutral-600">Fatturato azienda</div>
                                <div className="flex gap-[16px] w-[100%]">
                                    <Controller
                                        name="companyRevenue"
                                        control={form.control}
                                        render={({ field }) => (
                                            <NumericFormat
                                                prefix="€ "
                                                value={field.value ?? ""}
                                                thousandSeparator="."
                                                decimalSeparator=","
                                                decimalScale={2}
                                                fixedDecimalScale
                                                customInput={Input}
                                                onValueChange={(vals) => field.onChange(vals.floatValue ?? undefined)}
                                                placeholder="Fatturato"
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            {/* Website */}
                            <div className="flex gap-[24px] w-[100%]">
                                <div className="w-[240px] text-label-md font-medium text-neutral-600">Sito web</div>
                                <div className="flex gap-[16px] w-[100%]">
                                    <Controller
                                        name="companyWebsite"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Input {...field} value={field.value || ""} placeholder="Sito web" supportingText={form.formState.errors.companyWebsite?.message as string | undefined} error={!!form.formState.errors.companyWebsite} />
                                        )}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {role === UserRole.collaborator && (
                        <div className="flex gap-[24px] w-[100%] items-start">
                            <div className="w-[240px] text-label-md font-medium text-neutral-600">Cliente</div>
                            <div className="flex gap-[16px] w-[100%]">
                                <Controller
                                    name="customerId"
                                    control={form.control}
                                    render={({ field }) => (
                                        <SelectUser
                                            id="select-customer"
                                            role={UserRole.customer}
                                            value={field.value || null}
                                            onChange={(v) => field.onChange(v || "")}
                                            placeholder="Seleziona cliente"
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    )}

                    {/* File Upload */}
                    <div className="flex gap-[24px] w-[100%] items-start">
                        <div className="w-[240px] text-label-md font-medium text-neutral-600">Avatar</div>
                        <div className="flex gap-[16px] w-[100%]">
                            <Avatar
                                type="single"
                                size={"2xl"}
                                shape={"circle"}
                                imageUrl={preview || ''}
                                fullName={`${watch("name") || ""} ${watch("surname") || ""}`}
                                altTextInitials={`${(watch("name")?.[0] ?? "")}${(watch("surname")?.[0] ?? "")}`}
                            />
                            <FileUploader
                                id={"edit-user-file-uploader"}
                                style={"small"}
                                onChange={(files: File[]) => setFile(files[0])}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
